/**
 * Shared expiry checks for the presigned S3 URLs that AI blocks hand a user.
 *
 * Two renderers open one: SourceDocumentsRenderer opens a manufacturer PDF,
 * FileDownloadRenderer opens a generated export. Both face the same problem —
 * the URL in the block is a snapshot, and the only thing that reliably knows
 * when it stops working is the signature itself. The logic lived privately
 * inside SourceDocumentsRenderer; it lives here so the second consumer imports
 * it rather than growing a second, subtly different copy.
 */
/**
 * How close to expiry a link has to be before we refresh instead of opening it.
 *
 * A link with four seconds left passes every check and is still dead by the
 * time the tab has finished navigating, so "not expired yet" is not the same
 * question as "safe to hand over". Two minutes covers a slow tab open and a
 * user who clicks a beat after the card renders, without making a comfortably
 * fresh link pay for a round-trip it does not need.
 */
export const DOWNLOAD_REFRESH_BUFFER_MS = 2 * 60 * 1000;
/**
 * The SigV4 query params whose presence marks a URL as presigned at all.
 *
 * Any one of them is enough: a URL carrying none of them is an ordinary link
 * with no signature-based expiry to reason about.
 */
const SIGV4_PARAMS = ['X-Amz-Signature', 'X-Amz-Date', 'X-Amz-Expires', 'X-Amz-Algorithm'];
/**
 * What a URL's own signature says about whether it has aged out.
 *
 * Read from `X-Amz-Date` + `X-Amz-Expires` rather than from when the block
 * arrived: a conversation reloaded from history hands us a block with no clock
 * attached, and the signature itself is the only thing that knows when it stops
 * working.
 *
 * THREE outcomes, not two. Collapsing any pair of them is a live bug, and the
 * third one is the whole reason this returns `boolean | null`:
 *
 *   `true`  — presigned, and past its deadline (or inside `bufferMs`). Also
 *             what a presigned-but-UNREADABLE URL gets — half a signature, an
 *             unparseable date. That is the case the fail-closed posture is
 *             actually for: something tried to sign this and we cannot verify
 *             it, so one extra request beats a dead link.
 *   `false` — presigned, readable, still good.
 *   `null`  — NOT SIGNED AT ALL, so the signature makes no claim either way and
 *             it is not this function's job to invent one. Both callers reach
 *             this with real production values and they need OPPOSITE answers,
 *             which is why the posture lives with each caller —
 *             `isVerifiablyFreshPresignedUrl` and `needsFreshDownloadUrl` below
 *             — rather than being baked in here.
 *
 * `bufferMs` defaults to 0 so the plain "has it expired" question is unchanged.
 */
export function presignedUrlSaysExpired(url, bufferMs = 0) {
    let params;
    try {
        params = new URL(url).searchParams;
    }
    catch (_a) {
        // Not even a URL. "Unreadable" is not the same as "unsigned" — there is no
        // permanent link to be trusting here — so this fails closed rather than
        // returning null.
        return true;
    }
    if (!SIGV4_PARAMS.some((param) => params.has(param)))
        return null;
    const signedAt = params.get('X-Amz-Date');
    // Read the raw param and reject absence explicitly. `Number(null)` is 0, and
    // `Number.isFinite(0)` is true, so folding the lookup into Number() let a
    // half-signed URL through: expiresIn became 0, the comparison collapsed to
    // `now >= signedAt`, and a URL signed moments ago was reported FRESH — the
    // opposite of the fail-closed contract above, with isVerifiablyFreshPresignedUrl
    // then vouching for a URL S3 cannot serve. A zero or negative lifetime is
    // equally unusable, so it is rejected on the same branch.
    const expiresInRaw = params.get('X-Amz-Expires');
    const expiresIn = Number(expiresInRaw);
    if (!signedAt || expiresInRaw === null || !Number.isFinite(expiresIn) || expiresIn <= 0)
        return true;
    // X-Amz-Date is ISO-8601 basic format: 20260904T211500Z
    const iso = signedAt.replace(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/, '$1-$2-$3T$4:$5:$6Z');
    const signedMs = Date.parse(iso);
    if (Number.isNaN(signedMs))
        return true;
    return Date.now() + bufferMs >= signedMs + expiresIn * 1000;
}
/**
 * Can we positively prove this URL is a presigned link that is still good?
 *
 * One of the two postures over the `null` case above, and the one
 * SourceDocumentsRenderer needs. It has a mint endpoint and no reason to
 * gamble, so it asks "is this provably fresh?" and mints whenever the answer is
 * no — including for an unsigned URL it cannot vouch for. Its unsigned values
 * are not permanent links: rag-mcp emits the sentinel `rag-api-direct://<file>`
 * when no docs bucket is configured (`services/rag-mcp/src/citations.ts`,
 * `generatePresignedUrl`), and nothing can open that. Answering "still fine"
 * would hand a technician an unopenable URL instead of minting a real one from
 * `document_id`, so here the failure mode is one extra request.
 *
 * `needsFreshDownloadUrl` takes the opposite posture on the same `null`, because
 * FileDownloadRenderer is handed URLs that are legitimately permanent. Forcing
 * one predicate to serve both is how IP-15871's frontend fix nearly regressed 59
 * of the 63 exports in dev into a permanent, pointless refresh loop.
 *
 * Note this is `=== false`, not `!`: `null` is not "fresh", and a truthiness
 * check would quietly make it so.
 */
export function isVerifiablyFreshPresignedUrl(url, bufferMs = 0) {
    return presignedUrlSaysExpired(url, bufferMs) === false;
}
/**
 * Decide whether the expiry the server advertised alongside the URL has passed.
 *
 * The opposite failure posture to `presignedUrlSaysExpired`: a missing or
 * malformed timestamp is treated as NOT expired, because this value is a claim
 * about the URL rather than the URL itself, and refusing to open a link on the
 * strength of a garbled timestamp would strand a working download. The
 * signature check above is what fails closed.
 */
export function isAdvertisedExpiryPast(expiresAt, bufferMs = 0) {
    if (!expiresAt)
        return false;
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(expiresAt))
        return false;
    const expirationTime = new Date(expiresAt).getTime();
    if (Number.isNaN(expirationTime))
        return false;
    return expirationTime <= Date.now() + bufferMs;
}
/**
 * Should this link be refreshed before it is handed to the user?
 *
 * Two independent sources of truth, ORed, because each has been wrong on its
 * own. IP-15871: the backend advertised a 24-hour expiry while the AWS SDK
 * clamped the actual signature to under an hour, so `expires_at` said "live",
 * the button said "Download", and S3 answered with `Request has expired`. The
 * signature is the backstop for the advertised value being wrong again; the
 * advertised value is the backstop for a URL whose signature we cannot read.
 *
 * `?? false` is the posture opposite to `isVerifiablyFreshPresignedUrl`, and it
 * is deliberate. A download block's unsigned URL is a REAL, PERMANENT link — legacy
 * `ai_csv_exports` rows (no `s3_key`) are served from a plain API route with no
 * SigV4 params and a deliberately nil `download_url_expires_at`, and they were
 * 59 of the 63 exports in dev at the time of writing. Calling those expired put
 * a permanent "Refresh & Download" on a link that works fine, and since the
 * refresh returns the same unsigned URL the card would never settle.
 */
export function needsFreshDownloadUrl(url, expiresAt, bufferMs = 0) {
    var _a;
    return isAdvertisedExpiryPast(expiresAt, bufferMs) || ((_a = presignedUrlSaysExpired(url, bufferMs)) !== null && _a !== void 0 ? _a : false);
}
